import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ActionTabItem,
  ActionTabs,
  AppIcon,
  Coach,
  DeepDive,
  NUGGET_VARIANTS,
  PageHeader,
  useImageLoad,
  coachTransform,
  deepDiveTransform,
} from '@backrs_ui/common-ui';

import { appStrings as commonAppStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import checkIcon from '@backrs_ui/grow-ui/public/images/check-icon.png';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CalendarBookingModal } from '@backrs_ui/grow-ui/src/components/CalendarBookingModal/CalendarBookingModal';
import { NuggetCardList } from '@backrs_ui/grow-ui/src/components/NuggetCards/NuggetCardList';
import {
  handlePanelNuggetClick,
  handleShareWithNavigator,
} from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import { getBookmarkIdForNugget } from '@backrs_ui/grow-ui/src/pages/Bookmarks/pages/bookmarks.util';
import {
  usePostBookmarkMutation,
  useDeleteBookmarkMutation,
  useGetBookmarksQuery,
} from '@backrs_ui/grow-ui/src/redux/bookmarks/bookmarks.api';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  openPlaylistListStep,
  setNuggetToAdd,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';
import {
  CONSULTATION_PRODUCT_CODE,
  useGetProductByCodeQuery,
} from '@backrs_ui/grow-ui/src/redux/products/products.api';
import { selectUserId } from '@backrs_ui/grow-ui/src/redux/user/user';
import { CAL_TEAM_CONSULTATION_URL } from '@backrs_ui/grow-ui/src/settings';

import { Box, Button, Chip, Skeleton, SxProps, Theme, Typography } from '@mui/material';

const STRINGS = appStrings.modules.deepDives.detail;
const GROW_URL = process.env.REACT_APP_GROW_URL;

type DeepDiveDetailPageProps = Readonly<{
  deepDive: DeepDive;
  coaches: Coach[];
  isCoachesLoading?: boolean;
}>;

export const DeepDiveDetailPage = ({
  deepDive,
  coaches,
  isCoachesLoading,
}: DeepDiveDetailPageProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userId = useAppSelector(selectUserId);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const { data: bookmarks, isLoading: bookmarksLoading } = useGetBookmarksQuery(undefined);
  const [postBookmark] = usePostBookmarkMutation();
  const [deleteBookmark] = useDeleteBookmarkMutation();
  const { data: consultationProduct } = useGetProductByCodeQuery(CONSULTATION_PRODUCT_CODE);

  const bookmarkId = getBookmarkIdForNugget(bookmarks, deepDive?.id, NUGGET_VARIANTS.DEEP_DIVE);

  const handleBookmark = async () => {
    if (deepDive?.id) {
      if (bookmarkId) {
        await deleteBookmark(bookmarkId);
      } else {
        await postBookmark({
          type: 'deep-dive',
          itemId: deepDive.id,
        });
      }
    }
  };

  const handleShare = () => {
    handleShareWithNavigator({
      title: deepDive.name,
      text: deepDive.description || '',
      url: `${GROW_URL}/deep-dives/${deepDive.id}`,
    });
  };

  const handleScheduleCall = () => {
    setIsBookingModalOpen(true);
  };

  const handleBookingClose = () => {
    setIsBookingModalOpen(false);
  };

  const deepDiveNugget = useMemo(() => deepDiveTransform(deepDive), [deepDive]);

  const handleAddTask = () => {
    dispatch(openPlaylistListStep());
    dispatch(setNuggetToAdd(deepDiveNugget));
  };

  const coachNuggets = useMemo(() => coaches.map(coachTransform), [coaches]);

  const hasCoaches = coachNuggets.length > 0 || !!isCoachesLoading;

  const categoryBadges = deepDive.tags?.map((tag) => tag.name) ?? [];

  const heroImage = useImageLoad(deepDive.media);
  const hasMedia = !!deepDive.media && !heroImage.imageError;

  const [showCopySuccess, setShowCopySuccess] = useState(false);

  const actionButtons: ActionTabItem[] = [
    {
      id: 'deep-dive-bookmark-button',
      icon: bookmarkId ? 'BookmarkCheck' : 'Bookmark',
      onClick: handleBookmark,
      disabled: bookmarksLoading,
      tooltipHoverText: commonAppStrings.modules.actionTabs.bookmark,
    },
    {
      id: 'deep-dive-share-button',
      icon: 'Share06',
      onClick: handleShare,
      tooltipOnClickText: 'Link copied!',
      tooltipHoverText: commonAppStrings.modules.actionTabs.share,
    },
    {
      id: 'deep-dive-add-task-button',
      icon: 'BookmarkAdd',
      onClick: handleAddTask,
      tooltipHoverText: commonAppStrings.modules.actionTabs.task,
    },
  ];

  const price = STRINGS.price(deepDive.price);

  return (
    <Box sx={outerContainerSx}>
      <Box sx={scrollContainerWrapperSx}>
        <Box sx={mainContentSx}>
          {/* Hero Image with floating actions */}
          {hasMedia && (
            <Box sx={previewWrapperSx}>
              {!heroImage.imageLoaded && (
                <Skeleton variant="rectangular" animation="wave" sx={heroSkeletonSx} />
              )}
              <Box
                component="img"
                src={deepDive.media}
                alt={deepDive.name}
                sx={heroImageWithLoadSx(heroImage.imageLoaded)}
                {...heroImage.imageProps}
              />
              <Box sx={floatingActionsSx}>
                <ActionTabs
                  actions={actionButtons}
                  showCopySuccess={showCopySuccess}
                  onCopySuccessChange={setShowCopySuccess}
                />
              </Box>
            </Box>
          )}

          {/* Action buttons when no image */}
          {!hasMedia && (
            <Box sx={inlineActionsSx}>
              <ActionTabs
                actions={actionButtons}
                showCopySuccess={showCopySuccess}
                onCopySuccessChange={setShowCopySuccess}
              />
            </Box>
          )}

          {/* Header: title, description, schedule call */}
          <PageHeader
            title={deepDive.name}
            subtitle={deepDive.description}
            maxWidth={null}
            titleAdornment={
              price ? (
                <Typography variant="text-md-regular" sx={priceSx}>
                  {price}
                </Typography>
              ) : undefined
            }
            rightComponents={[
              <Button
                key="schedule-call"
                id="deep-dive-schedule-call-button-cta"
                hierarchy="Primary"
                size="md"
                icon="Leading"
                startIcon={
                  <AppIcon iconName="CalendarPlus01" diameter="20px" color="Colors/Base/White" />
                }
                onClick={handleScheduleCall}
                sx={scheduleCallButtonSx}
              >
                {STRINGS.scheduleACall}
              </Button>,
            ]}
          />

          {/* What's Included Section */}
          {deepDive.offerings && deepDive.offerings.length > 0 && (
            <Box sx={whatsIncludedSectionSx}>
              <Typography variant="text-xl-regular" sx={whatsIncludedTitleSx}>
                {STRINGS.whatsIncluded}
              </Typography>
              <Box sx={checkItemsContainerSx}>
                {deepDive.offerings.map((offering, index) => (
                  <Box key={index} sx={checkItemSx}>
                    <Box sx={checkIconContainerSx}>
                      <Box width="14px" height="14px" component="img" src={checkIcon} />
                    </Box>
                    <Typography variant="text-lg-regular" sx={checkItemTextSx}>
                      {offering}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Schedule a Consultation CTA */}
          <Button
            id="deep-dive-schedule-consultation-button"
            hierarchy="Primary"
            size="xl"
            icon="Leading"
            startIcon={
              <AppIcon iconName="CalendarPlus01" diameter="24px" color="Colors/Base/White" />
            }
            onClick={handleScheduleCall}
            sx={scheduleConsultationButtonSx}
          >
            {STRINGS.scheduleAConsultation}
          </Button>

          {/* Category Badges */}
          {categoryBadges.length > 0 && (
            <Box sx={categoriesSectionSx}>
              {categoryBadges.map((badge) => (
                <Chip
                  key={badge}
                  label={badge}
                  variant="pill"
                  size="lg"
                  icon={<AppIcon iconName="User02" diameter="12px" color="Brand/600" />}
                  type="leading"
                  sx={categoryChipSx}
                />
              ))}
            </Box>
          )}
        </Box>
        {hasCoaches && (
          <Box sx={coachesSectionSx}>
            <PageHeader
              title={STRINGS.coachesSidebar.title}
              subtitle={STRINGS.coachesSidebar.subtitle}
              isSubheader
              showBorder
              maxWidth={null}
            />
            <NuggetCardList
              nuggets={coachNuggets}
              onClick={(nugget) => handlePanelNuggetClick(nugget, navigate)}
              direction="scrollingRow"
            />
          </Box>
        )}
      </Box>

      {isBookingModalOpen && consultationProduct && (
        <CalendarBookingModal
          open={isBookingModalOpen}
          userId={userId}
          productId={consultationProduct.id}
          calendarUrl={CAL_TEAM_CONSULTATION_URL}
          contextProductId={deepDive.product_id}
          bookingContext={deepDive.name}
          onClose={handleBookingClose}
        />
      )}
    </Box>
  );
};

const outerContainerSx: SxProps<Theme> = {
  width: '100%',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100dvh',
};

const scrollContainerWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  overflowY: 'auto',
  pb: backrs.spacing['spacing-2xl'],
  px: backrs.spacing['spacing-xs'],
});

const mainContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '1280px',
  margin: { sm: '0 auto' },
  gap: backrs.spacing['spacing-3xl'],
});

const inlineActionsSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
};

const previewWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  aspectRatio: '16 / 9',
  maxHeight: '400px',
  borderRadius: backrs.radius['radius-xl'],
});

const floatingActionsSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: backrs.spacing['spacing-2xl'],
  right: backrs.spacing['spacing-2xl'],
  zIndex: 1,
});

const priceSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/600'],
  alignSelf: 'flex-end',
});

const scheduleCallButtonSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Brand/500'],
  border: `2px solid ${backrs.colors['Colors/Base/Black']}`,
  alignSelf: 'flex-start',
});

const heroImageWithLoadSx =
  (loaded: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: backrs.radius['radius-xl'],
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    opacity: loaded ? 1 : 0,
    transition: 'opacity 0.3s ease-in-out',
  });

const heroSkeletonSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  borderRadius: backrs.radius['radius-xl'],
});

const whatsIncludedSectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const whatsIncludedTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const checkItemsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  paddingLeft: backrs.spacing['spacing-lg'],
});

const checkItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
  alignItems: 'flex-start',
});

const checkIconContainerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '28px',
  height: '28px',
  borderRadius: '14px',
  backgroundColor: backrs.colors['Brand/100'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

const checkItemTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const scheduleConsultationButtonSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Brand/500'],
  border: `2px solid ${backrs.colors['Colors/Base/Black']}`,
  alignSelf: 'flex-start',
});

const categoriesSectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-md'],
  borderTop: `1px solid ${backrs.colors['Gray/200']}`,
  paddingTop: backrs.spacing['spacing-3xl'],
});

const categoryChipSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Brand/50'],
  border: `1px solid ${backrs.colors['Brand/200']}`,
  '& .MuiChip-label': {
    color: backrs.colors['Brand/700'],
  },
});

const coachesSectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  paddingTop: backrs.spacing['spacing-3xl'],
});
